import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

/**
 * BookNowDialog
 * -------------
 * Shown from any vendor's public profile ("Book now" button). The customer
 * types their name, email, optional phone, and a short message. Backend
 * relays it to the vendor's email via reply-to — the vendor's inbox is
 * never exposed to the customer.
 *
 * The `slug` prop identifies which vendor to contact. `vendorName` is used
 * for a warm confirmation copy after send.
 */
export default function BookNowDialog({ open, onOpenChange, slug, vendorName }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setMessage("");
    setWebsite(""); setSent(false); setBusy(false);
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error("A quick sentence or two — 10 characters minimum");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/vendors/by-slug/${slug}/contact`, {
        name: name.trim(), email: email.trim(), phone: phone.trim() || null,
        message: message.trim(), website,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
        || "Couldn't send that just now — please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="book-now-dialog">
        {sent ? (
          <div className="py-6 text-center" data-testid="book-now-sent">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--moss)" }} />
            <div className="font-serif text-2xl">Your message is on its way.</div>
            <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              {vendorName || "The maker"} will get your note by email and can reply to you
              directly at <span className="font-semibold">{email}</span>.
            </p>
            <Button
              type="button"
              className="btn-outline rounded-full mt-6"
              onClick={() => handleClose(false)}
              data-testid="book-now-close"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl">
                Book {vendorName || "this maker"}
              </DialogTitle>
              <DialogDescription>
                Tell them what you&apos;re after — a private event, a wholesale
                order, a collaboration. We&apos;ll relay it straight to their inbox.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-widest">Your name</Label>
                <Input
                  data-testid="book-now-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  maxLength={120}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Your email</Label>
                <Input
                  data-testid="book-now-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Phone (optional)</Label>
                <Input
                  data-testid="book-now-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={40}
                  placeholder="For urgent stuff"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Your message</Label>
                <Textarea
                  data-testid="book-now-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={5}
                  minLength={10}
                  maxLength={2000}
                  placeholder="Hi! I'd love to book you for a private tasting on…"
                />
                <div className="text-[11px] mt-1 text-right" style={{ color: "var(--ink-soft)" }}>
                  {message.length}/2000
                </div>
              </div>
              {/* Honeypot — hidden from real humans */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                style={{ position: "absolute", left: "-9999px", height: 0, width: 0 }}
                aria-hidden="true"
              />
              <Button
                type="submit"
                data-testid="book-now-submit"
                disabled={busy}
                className="btn-primary w-full py-6 rounded-full"
              >
                <Send className="h-4 w-4 mr-1.5" />
                {busy ? "Sending…" : "Send message"}
              </Button>
              <p className="text-[11px] text-center" style={{ color: "var(--ink-soft)" }}>
                Your email is only shared with {vendorName || "this maker"} so they can reply.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
